/**
 * ResponsiveSplit — two-pane side-by-side layout that collapses to a
 * single stacked column at or below 720px.
 *
 * Dual Pair Guarantee explainers render the left and right adjoint
 * side-by-side so the reader sees the pairing at a glance; on narrow
 * viewports the two panes stack. The active layout is exposed via the
 * `data-csk-split-layout` attribute (`side-by-side` | `stacked`) so the
 * behavior is assertable without driving a real media query.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery.js";

/**
 * Matches viewports of *exactly* 720px and narrower, mirroring the
 * collapse threshold used by compact side-by-side explainer panels.
 */
const NARROW_VIEWPORT_QUERY = "(max-width: 720px)";

export interface ResponsiveSplitProps {
  readonly left: React.ReactNode;
  readonly right: React.ReactNode;
  /** Accessible label for the grouping region. */
  readonly ariaLabel: string;
}

export const ResponsiveSplit = React.memo(function ResponsiveSplit({
  left,
  right,
  ariaLabel,
}: ResponsiveSplitProps): React.JSX.Element {
  const isNarrow = useMediaQuery(NARROW_VIEWPORT_QUERY);
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-csk-split-layout={isNarrow ? "stacked" : "side-by-side"}
      style={{
        display: "grid",
        gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
        gap: "0.75rem",
        alignItems: "stretch",
      }}
    >
      {left}
      {right}
    </div>
  );
});
